import React, { createContext, useContext, useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { transactionAPI } from '../services/api';
import cemeteryApi from '../services/cemeteryApi';
import { useToast } from './ToastContext';
import { useAuth } from './AuthContext'; // 🔧 AuthContext import 추가

const CemeterySettingsContext = createContext();

export const useCemeterySettings = () => {
  const context = useContext(CemeterySettingsContext);
  if (!context) {
    throw new Error('useCemeterySettings must be used within a CemeterySettingsProvider');
  }
  return context;
};

export const CemeterySettingsProvider = ({ children }) => {
  const queryClient = useQueryClient();
  const { success, error: showError } = useToast();
  const { user } = useAuth(); // 🔧 인증 상태 확인
  const [localSettings, setLocalSettings] = useState({});

  // 묘지 목록 조회 - 로그인된 사용자일 때만 실행
  const { data: cemeteries, isLoading: isLoadingCemeteries, refetch: refetchCemeteries } = useQuery({
    queryKey: ['cemeteries'],
    queryFn: () => cemeteryApi.getAll(),
    select: (response) => {
      console.log('묘지 목록 API 응답:', response);
      const cemeteryData = Array.isArray(response.data) ? response.data : [];
      console.log('묘지 목록 데이터:', cemeteryData);
      return cemeteryData;
    },
    enabled: !!user, // 🔧 로그인된 사용자일 때만 실행
    staleTime: 5 * 60 * 1000, // 5분간 데이터 신선도 유지
    cacheTime: 10 * 60 * 1000, // 10분간 캐시 유지
    refetchOnWindowFocus: false, // 윈도우 포커스 시 재요청 방지
    refetchOnMount: true, // 컴포넌트 마운트 시 재요청
    retry: 3, // 재시도 횟수 증가
    retryDelay: 1000 // 재시도 간격 설정
  });

  // 🔧 묘지 정보가 없을 때 자동으로 다시 로드 - 로그인된 사용자일 때만 실행
  useEffect(() => {
    if (user && !isLoadingCemeteries && (!cemeteries || cemeteries.length === 0)) {
      console.log('묘지 정보가 없음 - 자동으로 다시 로드 시도');
      setTimeout(() => {
        refetchCemeteries();
      }, 1000);
    }
  }, [user, cemeteries, isLoadingCemeteries, refetchCemeteries]);

  // 묘지 설정 조회 - 로그인된 사용자일 때만 실행
  const { data: cemeterySettings, isLoading: isLoadingSettings } = useQuery({
    queryKey: ['cemetery-settings'],
    queryFn: () => Promise.all(
      cemeteries?.map(cemetery => 
        transactionAPI.getCemeterySettings(cemetery.id).catch(() => null)
      ) || []
    ),
    select: (responses) => {
      console.log('묘지 설정 API 응답:', responses);
      const settings = {};
      responses.forEach((response, index) => {
        if (response && cemeteries?.[index]) {
          console.log(`묘지 ${cemeteries[index].id} 설정:`, response);
          settings[cemeteries[index].id] = response.data;
        }
      });
      console.log('최종 묘지 설정:', settings);
      return settings;
    },
    enabled: !!user && !!cemeteries && cemeteries.length > 0, // 🔧 로그인된 사용자이고 묘지가 있을 때만 실행
    staleTime: 5 * 60 * 1000, // 5분간 데이터 신선도 유지
    cacheTime: 10 * 60 * 1000, // 10분간 캐시 유지
    refetchOnWindowFocus: false, // 윈도우 포커스 시 재요청 방지
    refetchOnMount: true // 컴포넌트 마운트 시 재요청
  });

  // 묘지 설정 업데이트 뮤테이션
  const updateCemeterySettingsMutation = useMutation({
    mutationFn: ({ cemeteryId, data }) => transactionAPI.updateCemeterySettings(cemeteryId, data),
    onSuccess: (response, variables) => {
      console.log('묘지 설정 업데이트 성공:', { response, variables });
      // 서버 응답 데이터로 localSettings 업데이트 (response.data만 사용)
      const updatedData = response.data || variables.data;
      console.log('업데이트할 데이터:', updatedData);
      setLocalSettings(prev => {
        const newSettings = {
          ...prev,
          [variables.cemeteryId]: updatedData
        };
        console.log('새로운 localSettings:', newSettings);
        return newSettings;
      });
      success('묘지 설정이 업데이트되었습니다.');
    },
    onError: (error) => {
      showError('묘지 설정 업데이트에 실패했습니다.');
      console.error('묘지 설정 업데이트 실패:', error);
    }
  });



  // 로컬 설정 업데이트 함수
  const updateLocalSettings = (cemeteryId, field, value) => {
    setLocalSettings(prev => ({
      ...prev,
      [cemeteryId]: {
        ...prev[cemeteryId],
        [field]: value
      }
    }));
  };

  // 묘지 설정 저장 함수
  const saveCemeterySettings = (cemeteryId, settings) => {
    updateCemeterySettingsMutation.mutate({ cemeteryId, data: settings });
  };

  // 특정 묘지의 설정 가져오기
  const getCemeterySettings = (cemeteryId) => {
    const settings = cemeterySettings?.[cemeteryId] || {};
    
    console.log(`묘지 ${cemeteryId} 설정 조회:`, {
      cemeterySettings: cemeterySettings?.[cemeteryId],
      localSettings: localSettings[cemeteryId],
      settings
    });
    
    // localSettings에 해당 묘지의 설정이 있으면 사용, 없으면 서버 데이터 사용
    if (localSettings[cemeteryId]) {
      const localData = localSettings[cemeteryId];
      console.log(`묘지 ${cemeteryId} localSettings 사용:`, localData);
      
      // localData가 전체 응답 객체인지 확인하고 data 필드 추출
      if (localData.data) {
        return {
          usage_fee_per_sqm: localData.data.usage_fee_per_sqm || 0,
          management_fee_per_sqm: localData.data.management_fee_per_sqm || 0,
          management_fee_cycle: localData.data.management_fee_cycle || 5
        };
      }
      
      return localData;
    }
    
    // 서버 데이터에서 올바르게 추출
    const serverData = settings.data || settings;
    console.log(`묘지 ${cemeteryId} 서버 데이터 원본:`, serverData);
    
    const defaultSettings = {
      usage_fee_per_sqm: serverData.usage_fee_per_sqm || 0,
      management_fee_per_sqm: serverData.management_fee_per_sqm || 0,
      management_fee_cycle: serverData.management_fee_cycle || 5
    };
    console.log(`묘지 ${cemeteryId} 서버 데이터 사용:`, defaultSettings);
    return defaultSettings;
  };

  const value = {
    cemeteries,
    cemeterySettings,
    localSettings,
    isLoadingCemeteries,
    isLoadingSettings,
    updateLocalSettings,
    saveCemeterySettings,
    getCemeterySettings,
    updateCemeterySettingsMutation
  };

  return (
    <CemeterySettingsContext.Provider value={value}>
      {children}
    </CemeterySettingsContext.Provider>
  );
}; 